import {Component, computed, inject, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatOptionModule} from "@angular/material/core"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatInputModule} from "@angular/material/input"
import {MatMenu, MatMenuItem, MatMenuModule} from "@angular/material/menu"
import {MatSelectModule} from "@angular/material/select"
import {RouterLink} from "@angular/router"
import {FileDetailsFragment} from "@app/platform/components/files/file-details/file-details.generated"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {SectionComponent} from "@common/components/item/item-details/section/section.component"
import {ThumbnailComponent} from "@common/components/thumbnail/thumbnail.component"
import {IsLoadingDirective} from "@common/directives"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {FilesService} from "@common/services/files/files.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {ContentTypeModel, MutationUpdateDataObjectInput} from "@generated"
import {AssociatedJobsTableComponent} from "@platform/components/jobs/associated-jobs-table/associated-jobs-table.component"
import {BaseDetailsComponent} from "@platform/components/base/base-details/base-details.component"
import {TitleSectionComponent} from "@platform/components/details/title-section/title-section.component"
import {DeleteFileGQL, FileDetailsGQL, UpdateFileDetailsGQL} from "@platform/components/files/file-details/file-details.generated"
import {DetailsDialogLayoutComponent} from "@platform/components/layouts/details-dialog-layout/details-dialog-layout.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-file-details",
    imports: [
        DetailsDialogLayoutComponent,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatSelectModule,
        SectionComponent,
        IsLoadingDirective,
        MatButtonModule,
        RouterLink,
        ThumbnailComponent,
        RoutedDialogComponent,
        MatMenu,
        MatMenuItem,
        MatMenuModule,
        TitleSectionComponent,
        AssociatedJobsTableComponent,
    ],
    templateUrl: "./file-details.component.html",
    styleUrl: "./file-details.component.scss",
})
export class FileDetailsComponent extends BaseDetailsComponent<FileDetailsFragment, Omit<MutationUpdateDataObjectInput, "id">> {
    override _contentTypeModel = ContentTypeModel.DataObject
    override _fetchItem = fetchThrowingErrors(inject(FileDetailsGQL))
    override _updateItem = mutateThrowingErrors(inject(UpdateFileDetailsGQL))

    readonly organizations = inject(OrganizationsService)
    readonly deleteFile = inject(DeleteFileGQL)

    // A data object links to jobs via named relations (metadata/thumbnail processing), not assignments.
    readonly $processingJobs = computed(() => {
        const item = this.$item()
        if (!item) {
            return undefined
        }
        return [item.metadataProcessingJob, item.thumbnailProcessingJob].flatMap((job) => (job ? [job] : []))
    })

    async openJob(job: {id?: string | null} | undefined) {
        if (job?.id) {
            await this.router.navigate(["/jobs", job.id], {queryParamsHandling: "preserve"})
        }
    }

    downloadFile(): void {
        const downloadUrl = this.$item()?.downloadUrl
        if (downloadUrl) {
            FilesService.downloadFile("test", downloadUrl)
        }
    }

    performDelete = async () => {
        const item = this.$item()
        if (item) {
            await mutateThrowingErrors(this.deleteFile)(item)
            this.refresh.item(item)
            await this.closeModal()
        }
    }

    deleteConfirmationMessage() {
        const item = this.$item()
        return (
            (item ? `The file '${item.originalFileName}'` : "This job") +
            " will be deleted. This action <strong>cannot be undone</strong>.<br><br>Are you sure you want to continue?"
        )
    }

    protected readonly DialogSize = DialogSize
}
